import * as React from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuIcon from '@mui/icons-material/Menu';
import Container from '@mui/material/Container';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Icon from '@mui/material/Icon';
import { UserContext, UserRoleAdmin, UserRoleClient, UserRoleEmployee } from './Users';
import { CircularProgress, Divider, Grid, Stack } from '@mui/material';

const pages = ['explore', 'requests', 'quotes', 'clients', 'companies', 'suppliers'];
const settings = ['Profile', 'Users', 'Sign Out'];

export function EkasiServeAppBar() {
    const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null);
    const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
    const auth = React.useContext(UserContext);

    const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorElNav(event.currentTarget);
    };
    const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorElUser(event.currentTarget);
    };
    const navigate = useNavigate();

    const handleCloseNavMenu = () => {
        setAnchorElNav(null);
    };

    const handleCloseUserMenu = () => {
        setAnchorElUser(null);
    };

    const includeTabs = (page: string) => {
        if (auth.user.role === UserRoleAdmin) {
            return !['requests'].includes(page);
        } else if (auth.user.role === UserRoleEmployee) {
            return ['companies', 'clients', 'quotes', 'suppliers'].includes(page);
        } else if (auth.user.role === UserRoleClient) {
            return ['clients', 'quotes', 'requests'].includes(page);
        } else {
            return false;
        }
    }


    return (
        <AppBar position="sticky">
            <Container maxWidth="xl">
                <Toolbar disableGutters>
                    <Typography
                        variant="h6"
                        noWrap
                        component={Link}
                        to="/home"
                        sx={{
                            mr: 2,
                            display: { xs: 'none', md: 'flex' },
                            fontFamily: 'monospace',
                            fontWeight: 700,
                            letterSpacing: '.1rem',
                            color: 'inherit',
                            textDecoration: 'none',
                        }}
                    >
                        EKASI
                    </Typography>

                    <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
                        <IconButton
                            size="large"
                            aria-label="account of current user"
                            aria-controls="menu-appbar"
                            aria-haspopup="true"
                            onClick={handleOpenNavMenu}
                            color="inherit"
                        >
                            <MenuIcon />
                        </IconButton>
                        <Menu
                            id="menu-appbar"
                            anchorEl={anchorElNav}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'left',
                            }}
                            keepMounted
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'left',
                            }}
                            open={Boolean(anchorElNav)}
                            onClose={handleCloseNavMenu}
                            sx={{ display: { xs: 'block', md: 'none' } }}
                        >
                            {pages.filter(includeTabs).map((page) => (
                                <MenuItem key={page} onClick={handleCloseNavMenu}>
                                    <Typography component={Link} to={"/" + page} sx={{ textAlign: 'center' }}>{page}</Typography>
                                </MenuItem>
                            ))}
                        </Menu>
                    </Box>
                    <Typography
                        variant="h5"
                        noWrap
                        component={Link}
                        to="/home"
                        sx={{
                            mr: 2,
                            display: { xs: 'flex', md: 'none' },
                            flexGrow: 1,
                            fontFamily: 'monospace',
                            fontWeight: 700,
                            letterSpacing: '.1rem',
                            color: 'inherit',
                            textDecoration: 'none',
                        }}
                    >
                        EKASI
                    </Typography>
                    <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
                        {pages.filter(includeTabs).map((page) => (
                            <Button
                                key={page}
                                onClick={handleCloseNavMenu}
                                component={Link}
                                to={"/" + page}
                                sx={{ my: 2, color: 'inherit', display: 'block' }}
                            >
                                {page}
                            </Button>
                        ))}
                    </Box>
                    <Box sx={{ flexGrow: 0 }}>
                        <IconButton onClick={() => navigate(-1)} sx={{ p: 0, color: "inherit", marginRight: 2 }}>
                            <Icon sx={{ color: 'inherit', fontSize: 30 }}>arrow_back</Icon>
                        </IconButton>
                        <IconButton onClick={() => navigate(1)} sx={{ p: 0, color: "inherit", marginRight: 2 }}>
                            <Icon sx={{ color: 'inherit', fontSize: 30 }}>arrow_forward</Icon>
                        </IconButton>
                        <IconButton onClick={handleOpenUserMenu} sx={{ p: 0, color: "inherit" }}>
                            <Icon sx={{ color: 'inherit', fontSize: 40 }}>person</Icon>
                        </IconButton>
                        <Menu
                            sx={{ mt: '45px' }}
                            id="menu-appbar"
                            anchorEl={anchorElUser}
                            anchorOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            keepMounted
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            open={Boolean(anchorElUser)}
                            onClose={handleCloseUserMenu}
                        >
                            <Box marginLeft={4} marginRight={4} marginBottom={1}>
                                <Typography sx={{ textAlign: 'center' }}>{auth.user.username}</Typography>
                            </Box>
                            <Divider />
                            {settings.filter((setting) => auth.user.role === UserRoleAdmin || setting !== "Users").map((setting) => (
                                <MenuItem 
                                    component={Link}
                                    to={setting === "Profile" 
                                        ? "/users/" + auth.user.username 
                                        : setting === "Sign Out" 
                                            ? "/users/sign-out/" + auth.user.username 
                                            : setting === "Users"
                                                ? "/users"
                                                : "/"
                                    }
                                    key={setting} 
                                    onClick={handleCloseUserMenu}>
                                    <Typography sx={{ textAlign: 'center' }}>{setting}</Typography>
                                </MenuItem>
                            ))}
                        </Menu>
                    </Box>
                </Toolbar>
            </Container>
        </AppBar>
    );
}

export function isNumber(text: string): boolean {
    return /^[\d\s]+([.,]\d{1,2})?$/.test(text) === true
}

export function isInt(text: string): boolean {
    return /^\d+?$/.test(text) === true
}

export function parseNumber(text: string): number {
    var value = 0;
    var hasDecimal = false;
    var multiplier = 10000;
    for (var i = 0; i < text.length; i++) {
        if (multiplier <= 1) {
            break;
        }
        const c = text[i];
        switch (c) {
        case "0": value = value * 10 + 0; if (hasDecimal) multiplier /= 10; break;
        case "1": value = value * 10 + 1; if (hasDecimal) multiplier /= 10; break;
        case "2": value = value * 10 + 2; if (hasDecimal) multiplier /= 10; break;
        case "3": value = value * 10 + 3; if (hasDecimal) multiplier /= 10; break;
        case "4": value = value * 10 + 4; if (hasDecimal) multiplier /= 10; break;
        case "5": value = value * 10 + 5; if (hasDecimal) multiplier /= 10; break;
        case "6": value = value * 10 + 6; if (hasDecimal) multiplier /= 10; break;
        case "7": value = value * 10 + 7; if (hasDecimal) multiplier /= 10; break;
        case "8": value = value * 10 + 8; if (hasDecimal) multiplier /= 10; break;
        case "9": value = value * 10 + 9; if (hasDecimal) multiplier /= 10; break;
        case ".": case ",": hasDecimal = true; break;
        }
    }

    return value * (multiplier < 1 ? 1 : multiplier);
}

const randFormatter = new Intl.NumberFormat("en-ZA", { style: "currency", currency: "ZAR", minimumFractionDigits: 2 });
export function formatCurrency(num: number | undefined): string {
    if (num === undefined) {
        return "error";
    }
    return randFormatter.format(num / 10000);
}

const numFormatter = new Intl.NumberFormat("en-ZA", { minimumFractionDigits: 2 });
export function formatNumber(num: number | undefined): string {
    if (num === undefined) {
        return "error";
    }
    return numFormatter.format(num / 10000);
}

const percentageFormatter = new Intl.NumberFormat("en-ZA", { minimumFractionDigits: 1 });
export function formatPercentage(num: number | undefined): string {
    if (num === undefined) {
        return "error";
    }
    return percentageFormatter.format(num / 100); // use 100 to show percentages as a multiple of 100
}

export function selectAllOnFocus(event: React.FocusEvent<HTMLInputElement>) {
    const { target } = event;
    target.focus();
    if (target.selectionStart === target.value.length) {
        target.setSelectionRange(0, target.value.length);
    }
}

interface Identifiable {
    id: number;
}

export function updateObj(object: Identifiable | any, values: Identifiable | any): typeof object {
    if (object.id === values.id) {
        for (let key in values) {
            if (key === "id") {
                continue;
            }
            object[key] = values[key];
        }
    }
    return object;
}

export function equalObj(object: any, other: any): boolean {
    var result = true;
    for (let key in (object as any)) {
        if (other[key] !== undefined && other[key] === object[key]) {
            continue;
        } else {
            result = false;
            break;
        }
    }
    return result;
}

export function equalArrayOfObj(items: Array<any>, otherItems: Array<any>): boolean {
    if (items.length !== otherItems.length) {
        return false;
    }
    var result = true;
    for (let idx in items) {
        let other = otherItems[idx] as any;
        let item = items[idx] as any;
        result = equalObj(item, other);
        if (!result) {
            break;
        }
    }
    return result;
}

export function equalArray(items: Array<any>, otherItems: Array<any>): boolean {
    if (items.length !== otherItems.length) {
        return false;
    }
    var result = true;
    for (let idx in items) {
        let other = otherItems[idx] as any;
        let item = items[idx] as any;
        result = item === other;
        if (!result) {
            break;
        }
    }
    return result;
}

export function equalArrayOfArrayOfObj(items: Array<Array<any>>, otherItems: Array<Array<any>>): boolean {
    if (items.length !== otherItems.length) {
        return false;
    }
    var result = true;
    for (let idx in items) {
        let other = otherItems[idx] as Array<any>;
        let item = items[idx] as Array<any>;
        result = equalArrayOfObj(item, other);
        if (!result) {
            break;
        }
    }
    return result;
}

export function LoadingPage() {
    return (
        <Stack spacing={0} sx={{ height: "100vh"}}>
            <EkasiServeAppBar />
            <Grid component={Container} container spacing={2} sx={{ height: "100%", justifyContent: "center", alignContent: "center" }}>
                <CircularProgress size={100} />
            </Grid>
        </Stack>
    );
}


export type SetState<T> = React.Dispatch<React.SetStateAction<T>>

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallbackComponent: React.ComponentType<any>;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  render(): React.ReactNode {
    const { hasError } = this.state;
    const { children, fallbackComponent: FallbackComponent } = this.props;

    if (hasError) {
      return <FallbackComponent />;
    }

    return children;
  }
}